import type { SelectChangeEvent } from "@mui/material"
import { Box, FormControl, InputAdornment, MenuItem, Select, TextField } from "@mui/material"
import { FilterAltOutlined, KeyboardArrowDownOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { ChangeEvent, ReactElement, ReactNode } from "react"
import React, { memo, useMemo } from "react"

import { Button } from "@/components/common/Button"
import { TEXT_FIELD_STYLING_PROPS } from "@/constants/forms"
import { PermissionActionType } from "@/constants/permissions"
import {
  CompanyListCertificationSortField,
  CompanySortField,
  ContactSortField,
  LocationSortField,
} from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { SearchCategory } from "@/screens/Companies/types/enums"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams.ts"

interface CompanySearchBarProps {
  onShowFilter: () => void
  displayFilter?: boolean
}

const SelectIcon = memo(({ className }: { className: string }) => (
  <KeyboardArrowDownOutlined className={`text-gray-500 ${className}`} />
))

const hintMessageMap = new Map<SearchCategory, string>([
  [SearchCategory.COMPANY, "Search by Company Name or ID"],
  [SearchCategory.CONTACT, "Search by Contact’s Name"],
  [SearchCategory.LOCATION, "Search by Location name, Address, Company name"],
  [SearchCategory.CERTIFICATION, "Search by Company Name or ID number"],
])

export const CompaniesSearchBar = ({ onShowFilter, displayFilter }: CompanySearchBarProps): ReactElement => {
  const navigate = useNavigate()
  const { hasPermissions } = usePermissionManager()
  const currentSearch = useSearch({ from: "/app/companies/" })
  const searchHint = useMemo(() => hintMessageMap.get(currentSearch.category), [currentSearch.category])
  const hasMaintenancePermission = hasPermissions([PermissionActionType.VENDOR_MAINTENANCE])
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const queryValue = e.target.value

    const updatedSearchParams = getRefinedSearchParams({
      ...currentSearch,
      query: queryValue,
    })

    void navigate(updatedSearchParams)
  }

  const handleSearchCategoryDropdownChange = (event: SelectChangeEvent<SearchCategory>, _: ReactNode) => {
    const category = event.target.value
    let field: CompanySortField | ContactSortField | LocationSortField | CompanyListCertificationSortField =
      CompanySortField.Name
    switch (category) {
      case SearchCategory.COMPANY:
        field = CompanySortField.Name
        break
      case SearchCategory.CONTACT:
        field = ContactSortField.CompanyName
        break
      case SearchCategory.LOCATION:
        field = LocationSortField.LocationName
        break
      case SearchCategory.CERTIFICATION:
        field = CompanyListCertificationSortField.Name
        break
      default:
        break
    }
    const updatedSearchParams = getRefinedSearchParams({
      category,
      field,
      order: currentSearch.order,
    })
    void navigate(updatedSearchParams)
  }

  return (
    <Box className='flex space-x-4'>
      <div className='flex w-full max-w-lg'>
        <FormControl>
          <Select
            data-testid='search-category-select'
            value={currentSearch?.category ?? SearchCategory.COMPANY}
            variant='outlined'
            IconComponent={SelectIcon}
            sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            onChange={handleSearchCategoryDropdownChange}
          >
            <MenuItem data-testid='company-option' value={SearchCategory.COMPANY}>
              Company
            </MenuItem>
            <MenuItem data-testid='location-option' value={SearchCategory.LOCATION}>
              Location
            </MenuItem>
            <MenuItem data-testid='contact-option' value={SearchCategory.CONTACT}>
              Contact
            </MenuItem>
            {hasMaintenancePermission && (
              <MenuItem data-testid='certification-option' value={SearchCategory.CERTIFICATION}>
                Certifications
              </MenuItem>
            )}
          </Select>
        </FormControl>
        <TextField
          {...TEXT_FIELD_STYLING_PROPS}
          data-testid='search-input'
          defaultValue={currentSearch?.query}
          placeholder={searchHint}
          className='flex-1'
          sx={{ marginLeft: "-1px", "& fieldset": { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
            },
          }}
          onChange={handleInputChange}
        />
      </div>
      {displayFilter && (
        <Button
          data-testid='company-filter-button'
          aria-label='filter'
          appearance='outlined'
          onClick={onShowFilter}
          startIcon={<FilterAltOutlined />}
        >
          Filter
        </Button>
      )}
    </Box>
  )
}

export default CompaniesSearchBar
